// Placements — all active engagements + "ending in 30 days" alerts.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");
const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);
const daysLeft = (d?: string | null) => (d ? Math.ceil((new Date(d).getTime() - Date.now()) / 86400000) : null);

export default function PlacementsPage() {
  const all = useQuery({ queryKey: ["/placements", {}], queryFn: async () => (await api.get("/placements")).data.data as any[] });
  const alerts = useQuery({ queryKey: ["/placements/alerts/extensions"], queryFn: async () => (await api.get("/placements/alerts/extensions")).data.data as any[] });
  const rows = all.data ?? [];
  const ending = alerts.data ?? [];
  const showMoney = rows.some((r: any) => r.billRate != null || r.marginUsd != null);

  return (
    <>
      <PageHeader title="Placements" description="Active engagements and upcoming end dates." />
      <div className="space-y-4 p-6">
        {ending.length > 0 && (
          <Card className="border-amber-300">
            <CardHeader><CardTitle className="text-base text-amber-700 dark:text-amber-400">Ending in the next 30 days ({ending.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-1">
                {ending.map((p: any) => {
                  const dl = daysLeft(p.endDate);
                  return (
                    <div key={p.id} className="flex items-center gap-3 rounded border bg-card px-3 py-1.5 text-sm">
                      <Link to={`/candidates/${p.candidate?.id}`} className="font-medium hover:underline">{p.candidate?.name ?? "—"}</Link>
                      <span className="text-muted-foreground">@</span>
                      <Link to={`/job-orders/${p.jobOrder?.id}`} className="hover:underline">{p.jobOrder?.title ?? "—"}</Link>
                      <Badge variant={dl != null && dl <= 7 ? "destructive" : "secondary"} className="ml-auto">{dl != null ? `${dl}d left` : "—"}</Badge>
                      <span className="text-muted-foreground">ends {fmtDate(p.endDate)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader><CardTitle className="text-base">All placements</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <Thead><Tr><Th>Candidate</Th><Th>Job Order</Th><Th>Start</Th><Th>End</Th><Th>Status</Th>{showMoney && <Th>Margin</Th>}</Tr></Thead>
              <Tbody>
                {all.isLoading && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!all.isLoading && rows.length === 0 && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">No placements.</Td></Tr>}
                {rows.map((p: any) => (
                  <Tr key={p.id}>
                    <Td className="font-medium"><Link to={`/candidates/${p.candidate?.id}`} className="hover:underline">{p.candidate?.name ?? "—"}</Link></Td>
                    <Td><Link to={`/job-orders/${p.jobOrder?.id}`} className="hover:underline">{p.jobOrder?.title ?? "—"}</Link></Td>
                    <Td className="text-sm">{fmtDate(p.startDate)}</Td>
                    <Td className="text-sm">{fmtDate(p.endDate)}</Td>
                    <Td><Badge variant="secondary">{p.status}</Badge></Td>
                    {showMoney && <Td className="text-sm">{money(p.marginUsd) ?? "—"}</Td>}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
